import * as React from "react";
import TextHybrid from "./TextHybrid";
import type { TypeTextProps } from "./TextTypes";

export interface TypeTextComponent
  extends React.ForwardRefExoticComponent<
    TypeTextProps & React.RefAttributes<HTMLSpanElement>
  > {
  Headline: React.FC<TypeTextProps>;
  SubHeadline: React.FC<TypeTextProps>;
  SmallSubHeadline: React.FC<TypeTextProps>;
  Byline: React.FC<TypeTextProps>;
  SmallByline: React.FC<TypeTextProps>;
  BodyCopy: React.FC<TypeTextProps>;
  SmallBodyCopy: React.FC<TypeTextProps>;
}

/**
 * Build a typographic sub-component that renders the base hybrid `Text` with a
 * Seeds variant class (e.g. `seeds-text-headline`) merged into `className`.
 * Routing through `TextHybrid` keeps `data-qa-text` injection and lands on the
 * Tailwind path as long as no typography system props are passed.
 */
const makeVariant = (
  variantClass: string,
  displayName: string
): React.FC<TypeTextProps> => {
  const Variant: React.FC<TypeTextProps> = ({ className, ...props }) => (
    <TextHybrid
      className={[variantClass, className].filter(Boolean).join(" ")}
      {...props}
    />
  );
  Variant.displayName = displayName;
  return Variant;
};

const Headline = makeVariant("seeds-text-headline", "Text.Headline");
const SubHeadline = makeVariant("seeds-text-subheadline", "Text.SubHeadline");
const SmallSubHeadline = makeVariant(
  "seeds-text-small-subheadline",
  "Text.SmallSubHeadline"
);
const Byline = makeVariant("seeds-text-byline", "Text.Byline");
const SmallByline = makeVariant("seeds-text-small-byline", "Text.SmallByline");
const BodyCopy = makeVariant("seeds-text-body", "Text.BodyCopy");
const SmallBodyCopy = makeVariant(
  "seeds-text-small-body",
  "Text.SmallBodyCopy"
);

const Text = Object.assign(TextHybrid, {
  Headline,
  SubHeadline,
  SmallSubHeadline,
  Byline,
  SmallByline,
  BodyCopy,
  SmallBodyCopy,
});

export default Text;
